import { BrowserRouter as Router, Navigate, Routes, Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Toaster } from 'sonner';
import { toasterOptions } from './utils/toast';
import AuthInitializer from './components/common/AuthInitializer';
import TrialExpiredGate from './components/common/TrialExpiredGate';
import PlanRenewalGate from './components/common/PlanRenewalGate';
import ProtectedRoute from './components/common/ProtectedRoute';
import PageLoadingFallback from './components/common/PageLoadingFallback';
import { CompanyAccessProvider } from './context/CompanyAccessContext';
import AppShellLayout from './components/layout/AppShellLayout';

const Dashboard = lazy(() => import('./Pages/Dashboard'));
const JobSearch = lazy(() => import('./Pages/JobSearch'));
const Favorites = lazy(() => import('./Pages/Favorites'));
const JobListings = lazy(() => import('./Pages/JobListings'));
const CompanyExpiredJobs = lazy(() => import('./Pages/CompanyExpiredJobs'));
const JobListingDetail = lazy(() => import('./Pages/JobListingDetail'));
const AddJobListing = lazy(() => import('./Pages/AddJobListing'));
const EditJobListing = lazy(() => import('./Pages/EditJobListing'));
const Candidates = lazy(() => import('./Pages/Candidates'));
const CandidateDetail = lazy(() => import('./Pages/CandidateDetail'));
const CandidateRecommendations = lazy(() => import('./Pages/CandidateRecommendations'));
const AddCandidate = lazy(() => import('./Pages/AddCandidate'));
const EditCandidate = lazy(() => import('./Pages/EditCandidate'));
const CompanyUsers = lazy(() => import('./Pages/CompanyUsers'));
const CompanyUserDetail = lazy(() => import('./Pages/CompanyUserDetail'));
const CompanyUserActivity = lazy(() => import('./Pages/CompanyUserActivity'));
const CompanyUserAttributions = lazy(() => import('./Pages/CompanyUserAttributions'));
const CandidateInviteLinks = lazy(() => import('./Pages/CandidateInviteLinks'));
const CandidateSelfForm = lazy(() => import('./Pages/CandidateSelfForm'));
const JobFairLinks = lazy(() => import('./Pages/JobFairLinks'));
const JobFairPublicForm = lazy(() => import('./Pages/JobFairPublicForm'));
const PublicInvoice = lazy(() => import('./Pages/PublicInvoice'));
const PlanUsage = lazy(() => import('./Pages/PlanUsage'));
const FullUsageHistory = lazy(() => import('./Pages/FullUsageHistory'));
const PlanBilling = lazy(() => import('./Pages/PlanBilling'));
const Support = lazy(() => import('./Pages/Support'));
const CompanySettings = lazy(() => import('./Pages/CompanySettings'));
const AdminDashboard = lazy(() => import('./Pages/AdminDashboard'));
const Companies = lazy(() => import('./Pages/Companies'));
const AddCompany = lazy(() => import('./Pages/AddCompany'));
const EditCompany = lazy(() => import('./Pages/EditCompany'));
const CompanyDetails = lazy(() => import('./Pages/CompanyDetails'));
const CompanyInsights = lazy(() => import('./Pages/CompanyInsights'));
const CompanyInsightsJobListings = lazy(() => import('./Pages/CompanyInsightsJobListings'));
const CompanyInsightsActivity = lazy(() => import('./Pages/CompanyInsightsActivity'));
const Plans = lazy(() => import('./Pages/Plans'));
const AddPlan = lazy(() => import('./Pages/AddPlan'));
const EditPlan = lazy(() => import('./Pages/EditPlan'));
const Staff = lazy(() => import('./Pages/Staff'));
const StaffDetails = lazy(() => import('./Pages/StaffDetails'));
const StaffJobs = lazy(() => import('./Pages/StaffJobs'));
const StaffCompanies = lazy(() => import('./Pages/StaffCompanies'));
const StaffTickets = lazy(() => import('./Pages/StaffTickets'));
const StaffActivity = lazy(() => import('./Pages/StaffActivity'));
const Roles = lazy(() => import('./Pages/Roles'));
const AdminRoleEditor = lazy(() => import('./Pages/AdminRoleEditor'));
const AdminRoleView = lazy(() => import('./Pages/AdminRoleView'));
const JobSources = lazy(() => import('./Pages/JobSources'));
const ScheduledFetch = lazy(() => import('./Pages/ScheduledFetch'));
const FetchedJobs = lazy(() => import('./Pages/FetchedJobs'));
const ExpiredJobs = lazy(() => import('./Pages/ExpiredJobs'));
const AdminAddFetchedJob = lazy(() => import('./Pages/AdminAddFetchedJob'));
const AdminEditFetchedJob = lazy(() => import('./Pages/AdminEditFetchedJob'));
const AdminFetchedJobDetails = lazy(() => import('./Pages/AdminFetchedJobDetails'));
const SystemLogs = lazy(() => import('./Pages/SystemLogs'));
const SystemSettings = lazy(() => import('./Pages/SystemSettings'));
const SupportTickets = lazy(() => import('./Pages/SupportTickets'));
const AdminAccounts = lazy(() => import('./Pages/AdminAccounts'));
const AdminAccountSubscriptionHistory = lazy(() => import('./Pages/AdminAccountSubscriptionHistory'));
const AdminAccountCompanyInvoices = lazy(() => import('./Pages/AdminAccountCompanyInvoices'));
const CareersAdmin = lazy(() => import('./Pages/CareersAdmin'));
const AdminBlogManagement = lazy(() => import('./Pages/AdminBlogManagement'));
const AdminBlogPostEditor = lazy(() => import('./Pages/AdminBlogPostEditor'));
const AdminFeatureManagement = lazy(() => import('./Pages/AdminFeatureManagement'));
const AdminFeatureEditor = lazy(() => import('./Pages/AdminFeatureEditor'));
const AdminFaqManagement = lazy(() => import('./Pages/AdminFaqManagement'));
const AdminIndustryManagement = lazy(() => import('./Pages/AdminIndustryManagement'));
const AdminMasterSkillManagement = lazy(() => import('./Pages/AdminMasterSkillManagement'));
const AdminTestimonialManagement = lazy(() => import('./Pages/AdminTestimonialManagement'));
const AdminTermsManagement = lazy(() => import('./Pages/AdminTermsManagement'));
const AdminPrivacyManagement = lazy(() => import('./Pages/AdminPrivacyManagement'));
const AdminRefundManagement = lazy(() => import('./Pages/AdminRefundManagement'));
const AdminTutorialManagement = lazy(() => import('./Pages/AdminTutorialManagement'));
const AdminNewsletterManagement = lazy(() => import('./Pages/AdminNewsletterManagement'));
const AdminNoAccess = lazy(() => import('./Pages/AdminNoAccess'));

function App() {
  return (
    <Router>
      <AuthInitializer />
      <Toaster {...toasterOptions} className="app-toaster !z-[100]" />
      <CompanyAccessProvider>
        <PlanRenewalGate>
        <TrialExpiredGate>
      <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
        {/* Company routes — shared layout keeps sidebar mounted between navigations */}
        <Route
          element={
            <ProtectedRoute allowedRoles={['company', 'company_staff']}>
              <AppShellLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Dashboard />} />
          <Route path="/job-search" element={<JobSearch />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/job-listings" element={<JobListings />} />
          <Route path="/expired-jobs" element={<CompanyExpiredJobs />} />
          <Route path="/job-listings/new" element={<AddJobListing />} />
          <Route path="/job-listings/:id/edit" element={<EditJobListing />} />
          <Route path="/job-listings/:id" element={<JobListingDetail />} />
          <Route path="/candidates" element={<Candidates />} />
          <Route path="/candidates/:id/edit" element={<EditCandidate />} />
          <Route path="/candidates/:id" element={<CandidateDetail />} />
          <Route path="/candidates/:id/recommendations" element={<CandidateRecommendations />} />
          <Route path="/add-candidate" element={<AddCandidate />} />
          <Route path="/company-users" element={<CompanyUsers />} />
          <Route path="/company-users/:id/activity" element={<CompanyUserActivity />} />
          <Route path="/company-users/:id/attributions" element={<CompanyUserAttributions />} />
          <Route path="/company-users/:id" element={<CompanyUserDetail />} />
          <Route path="/candidate-links" element={<CandidateInviteLinks />} />
          <Route path="/job-fair" element={<JobFairLinks />} />
          <Route
            path="/plan-usage"
            element={
              <ProtectedRoute allowedRoles={['company']}>
                <PlanUsage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/usage-history"
            element={
              <ProtectedRoute allowedRoles={['company']}>
                <FullUsageHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute allowedRoles={['company']}>
                <PlanBilling />
              </ProtectedRoute>
            }
          />
          <Route path="/support" element={<Support />} />
          <Route path="/settings" element={<CompanySettings />} />
        </Route>

        <Route path="/candidate-form/:token" element={<CandidateSelfForm />} />
        <Route path="/job-fair-form/:token" element={<JobFairPublicForm />} />
        <Route path="/invoice/:token" element={<PublicInvoice />} />
        {/* Admin routes — shared layout keeps sidebar mounted between navigations */}
        <Route
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'manager', 'staff']}>
              <AppShellLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/admin/no-access" element={<AdminNoAccess />} />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="dashboard.view"
              >
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/companies"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="companies.view"
              >
                <Companies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/companies/new"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="companies.create"
              >
                <AddCompany />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/companies/:id/edit"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="companies.update"
              >
                <EditCompany />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/companies/:id/insights/jobs"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="companies.view"
              >
                <CompanyInsightsJobListings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/companies/:id/insights/activity"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="companies.view"
              >
                <CompanyInsightsActivity />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/companies/:id/insights"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="companies.view"
              >
                <CompanyInsights />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/companies/:id"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="companies.view"
              >
                <CompanyDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/plans/new"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="plans.view"
              >
                <AddPlan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/plans/:id/edit"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="plans.view"
              >
                <EditPlan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/plans"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="plans.view"
              >
                <Plans />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/staff"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="staff.view"
              >
                <Staff />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/staff/:id/jobs"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="staff.view"
              >
                <StaffJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/staff/:id/companies"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="staff.view"
              >
                <StaffCompanies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/staff/:id/tickets"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="staff.view"
              >
                <StaffTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/staff/:id/activity"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="staff.view"
              >
                <StaffActivity />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/staff/:id"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="staff.view"
              >
                <StaffDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="roles.view"
              >
                <Roles />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles/new"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="roles.create"
              >
                <AdminRoleEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles/:id/edit"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="roles.update"
              >
                <AdminRoleEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles/:id"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="roles.view"
              >
                <AdminRoleView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/job-sources"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="job_sources.view"
              >
                <JobSources />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/scheduled-fetch"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="scheduled_fetch.view"
              >
                <ScheduledFetch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/fetched-jobs"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="fetched_jobs.view"
              >
                <FetchedJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/expired-jobs"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="fetched_jobs.view"
              >
                <ExpiredJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/fetched-jobs/new"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="fetched_jobs.manage"
              >
                <AdminAddFetchedJob />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/fetched-jobs/:id"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="fetched_jobs.view"
              >
                <AdminFetchedJobDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/fetched-jobs/:id/edit"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="fetched_jobs.manage"
              >
                <AdminEditFetchedJob />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/system-logs"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="system_logs.view"
              >
                <SystemLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/system-settings"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="system_settings.view"
              >
                <SystemSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/accounts"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="accounts.view"
              >
                <AdminAccounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/accounts/:companyId/history"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="accounts.view"
              >
                <AdminAccountSubscriptionHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/accounts/:companyId/invoices"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="accounts.view"
              >
                <AdminAccountCompanyInvoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/support"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="support.view"
              >
                <SupportTickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/careers"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="careers_jobs.view"
              >
                <CareersAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blogs"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="blogs.view"
              >
                <AdminBlogManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blogs/new"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="blogs.create"
              >
                <AdminBlogPostEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blogs/:id/edit"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="blogs.update"
              >
                <AdminBlogPostEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/features"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="features.view"
              >
                <AdminFeatureManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/features/new"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="features.create"
              >
                <AdminFeatureEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/features/:id/edit"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="features.update"
              >
                <AdminFeatureEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tutorials"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="tutorials.view"
              >
                <AdminTutorialManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/industries"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="industries.view"
              >
                <AdminIndustryManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/master-skills"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="master_skills.view"
              >
                <AdminMasterSkillManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/faqs"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="faqs.view"
              >
                <AdminFaqManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/testimonials"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="testimonials.view"
              >
                <AdminTestimonialManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/terms"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="terms.view"
              >
                <AdminTermsManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/privacy"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="privacy.view"
              >
                <AdminPrivacyManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/refund"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager', 'staff']}
                requiredPermissions="refund.view"
              >
                <AdminRefundManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/newsletter"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'manager']}
                requiredPermissions="newsletter.view"
              >
                <AdminNewsletterManagement />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <Navigate to="/" replace />
            </ProtectedRoute>
          }
        />
      </Routes>
      </Suspense>
        </TrialExpiredGate>
        </PlanRenewalGate>
      </CompanyAccessProvider>
    </Router>
  );
}

export default App;
